/**
 * The public-sharing toggle and the link it surfaces (§11, Sharing).
 *
 * §11 asks for one thing above all: "Make it easy to see, at a glance, that a
 * garage is public." That is why public state is loud here — a full-width amber
 * banner, not a checkbox in a settings list — and why the marker is repeated on
 * the garage switcher, which is on screen from every route.
 *
 * The other half is honesty about revocation. Turning sharing off does not
 * pause the link, it destroys the token; turning it back on mints a different
 * one. Anyone still holding the old URL gets a 404 forever. The copy says so
 * before the click, not after.
 */

import { useState } from 'react';
import { Check, Copy, Globe, Link2, Lock, TriangleAlert } from 'lucide-react';

import type { GarageDto } from '@svcdue/contracts';

import { useSetPublic, useUpdateGarage } from '../api/hooks';
import { errorMessage } from '../components/states';
import { usePermissions } from './GarageProvider';

/**
 * The link, assembled server-side (`SetPublicResponse.publicUrl`) — but that
 * only comes back from the toggle call. On a fresh page load we hold the token
 * from `GET /garages` and no URL, so the origin is prepended here. Same shape
 * the API builds (`${webOrigin}/g/${token}`), and the SPA is by definition
 * served from the origin the link should point at.
 */
const publicUrlFor = (token: string): string => `${window.location.origin}/g/${token}`;

export function ShareToggle({ garage }: { garage: GarageDto }) {
  const { canManageSharing } = usePermissions();
  const setPublic = useSetPublic(garage.id);
  const updateGarage = useUpdateGarage(garage.id);
  const [confirming, setConfirming] = useState(false);

  /**
   * Crew see the state, never the control.
   *
   * They also never receive the token — the API withholds it from anyone but
   * the owner (routes/garages.ts `toGarageDto`), because a crew member who
   * leaves should not keep a working link. So this branch is not just hiding a
   * button: there is genuinely nothing here to show them.
   */
  if (!canManageSharing) {
    return garage.isPublic ? (
      <div className="share-banner share-on">
        <Globe size={15} />
        <div>
          <b>This garage is public</b>
          <span>Anyone with the link can read it. Only the owner can change this.</span>
        </div>
      </div>
    ) : null;
  }

  const token = garage.publicToken;

  return (
    <div className="share">
      <div className={'share-banner' + (garage.isPublic ? ' share-on' : ' share-off')}>
        {garage.isPublic ? <Globe size={15} /> : <Lock size={15} />}
        <div>
          <b>{garage.isPublic ? 'This garage is public' : 'This garage is private'}</b>
          <span>
            {garage.isPublic
              ? 'Anyone with the link can read your cars, timeline and status. No member names or emails are shared.'
              : 'Only you and your crew can see it. Sharing is off by default.'}
          </span>
        </div>

        {garage.isPublic ? (
          <button className="act act-danger" onClick={() => setConfirming(true)} disabled={setPublic.isPending}>
            Stop sharing
          </button>
        ) : (
          <button className="act" onClick={() => setPublic.mutate(true)} disabled={setPublic.isPending}>
            <Link2 size={12} /> {setPublic.isPending ? 'Creating link…' : 'Create public link'}
          </button>
        )}
      </div>

      {garage.isPublic && token && <PublicLink url={publicUrlFor(token)} />}

      {/* Only meaningful while public, and off by default: a shared garage shows
          the fleet and forecast, but the build sheet — part numbers and where to
          buy them — is exposed only when the owner ticks this. Flipping it does
          not change the link. */}
      {garage.isPublic && (
        <label className="share-opt">
          <input
            type="checkbox"
            checked={garage.publicShowMaintenance}
            disabled={updateGarage.isPending}
            onChange={(e) => updateGarage.mutate({ publicShowMaintenance: e.target.checked })}
          />
          <span>
            <b>Show maintenance details</b>
            <span>Include each part's number and reorder link on the public page. Off by default.</span>
          </span>
        </label>
      )}

      {(setPublic.isError || updateGarage.isError) && (
        <div className="field-err">{errorMessage(setPublic.error ?? updateGarage.error)}</div>
      )}

      {confirming && (
        <div className="share-confirm">
          <TriangleAlert size={14} />
          <div>
            <b>Stop sharing this garage?</b>
            {/* The point of the confirm: this is destructive in a way "off"
                does not sound. */}
            <span>The current link stops working permanently. Turning sharing back on creates a different link.</span>
          </div>
          <button className="act act-ghost" onClick={() => setConfirming(false)}>
            Cancel
          </button>
          <button
            className="act act-danger"
            onClick={() => {
              setPublic.mutate(false);
              setConfirming(false);
            }}
          >
            Stop sharing
          </button>
        </div>
      )}
    </div>
  );
}

function PublicLink({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access is refused in some contexts and there is no fallback
      // worth building — the input beside the button holds the full URL and is
      // selectable, so the user can still copy it by hand.
    }
  };

  return (
    <div className="share-link">
      <input className="input share-url" value={url} readOnly onFocus={(e) => e.target.select()} aria-label="Public link" />
      <button className="act" onClick={copy}>
        {copied ? (
          <>
            <Check size={12} /> Copied
          </>
        ) : (
          <>
            <Copy size={12} /> Copy
          </>
        )}
      </button>
      <a className="act act-ghost" href={url} target="_blank" rel="noreferrer">
        Preview
      </a>
    </div>
  );
}
